/**
 * Gate Firebase + Usuarios Portal (módulo analytics).
 * Entrada: solo Google (sin Turnstile). Expone { email } cuando la sesión es válida.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import LoginPage from '../modules/auth/pages/LoginPage.jsx';
import {
  peekAuthUser,
  requireFirebaseSession,
  getIdToken,
  mapAnalyticsSecurityError,
  mapFirebaseAuthError,
  consumeGoogleRedirectResult,
  logoutAnalytics
} from '../services/firebase-auth.js';
import { fetchSalesAnalyticsPermissions } from '../services/analytics-api.js';

export function AuthGate({ children }) {
  const [phase, setPhase] = useState('booting');
  const [email, setEmail] = useState('');
  const [resumeEmail, setResumeEmail] = useState('');
  const [loginError, setLoginError] = useState('');

  const enterApp = useCallback(async () => {
    await requireFirebaseSession();
    const token = await getIdToken(false);
    const perm = await fetchSalesAnalyticsPermissions(token);
    if (!perm || perm.allowed !== true) {
      throw new Error(
        (perm && perm.reason) ||
          'No tienes permiso para acceder a Analytics. Contacta al administrador.'
      );
    }
    setEmail((perm.email || '').toLowerCase());
    setResumeEmail('');
    setPhase('app');
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        try {
          await consumeGoogleRedirectResult();
        } catch (redirectErr) {
          if (cancelled) return;
          setLoginError(
            mapFirebaseAuthError(redirectErr) ||
              mapAnalyticsSecurityError(redirectErr) ||
              'No se pudo completar el ingreso con Google.'
          );
          setResumeEmail('');
          setPhase('login');
          return;
        }
        const user = await peekAuthUser(8000);
        if (cancelled) return;
        if (!user) {
          setResumeEmail('');
          setPhase('login');
          return;
        }
        // Sesión Google ya activa → entrar sin captcha.
        setResumeEmail(String(user.email || '').toLowerCase());
        try {
          await enterApp();
        } catch (err) {
          if (cancelled) return;
          setLoginError(
            mapAnalyticsSecurityError(err) || (err && err.message) || 'No se pudo iniciar.'
          );
          setPhase('login');
        }
      } catch (err) {
        if (cancelled) return;
        setLoginError(mapAnalyticsSecurityError(err) || (err && err.message) || 'No se pudo iniciar.');
        setResumeEmail('');
        setPhase('login');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enterApp]);

  const session = useMemo(() => ({ email }), [email]);

  if (phase === 'booting' || (phase === 'app' && !email)) {
    return (
      <div className="analytics-login is-busy">
        <div className="analytics-login-card" aria-busy="true">
          <p className="analytics-eyebrow">GI Capital</p>
          <h1>Analytics</h1>
          <p className="analytics-login-lead analytics-login-lead-busy">
            <span className="analytics-btn-spinner" aria-hidden="true" />
            Comprobando sesión…
          </p>
        </div>
      </div>
    );
  }

  if (phase === 'login') {
    return (
      <LoginPage
        resumeEmail={resumeEmail}
        error={loginError}
        onSuccess={async () => {
          setLoginError('');
          try {
            await enterApp();
          } catch (err) {
            const mapped =
              mapAnalyticsSecurityError(err) || (err && err.message) || 'No se pudo iniciar.';
            setLoginError(mapped);
            await logoutAnalytics().catch(() => {});
            setResumeEmail('');
            throw err;
          }
        }}
      />
    );
  }

  return typeof children === 'function' ? children(session) : children;
}
